import type { ApiErrorBody, Locale } from "@dteba/shared";

/** In dev the Vite proxy keeps us same-origin; in prod this is the API host. */
const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const ADMIN_TOKEN_KEY = "dteba.admin-token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Field path → message, ready to hand to react-hook-form's setError. */
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token: string | null) {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  locale?: Locale;
  query?: Record<string, string | number | boolean | undefined>;
  /** Attaches the CMS bearer token. */
  admin?: boolean;
  signal?: AbortSignal;
};

/**
 * Multipart upload. Deliberately separate from apiRequest: the body must stay
 * a FormData so the browser can set its own multipart boundary, which means no
 * Content-Type header of our own.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  options: { admin?: boolean; signal?: AbortSignal } = {},
): Promise<T> {
  const url = new URL(`${BASE_URL}/api${path}`, window.location.origin);
  const headers: Record<string, string> = { Accept: "application/json" };

  if (options.admin !== false) {
    const token = getAdminToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { method: "POST", headers, body: form, signal: options.signal });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      payload?.error.code ?? "UNKNOWN",
      payload?.error.message ?? "The upload failed. Please try again.",
      payload?.error.fields,
    );
  }

  const payload = (await response.json()) as { data: T };
  return payload.data;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, locale, query, admin = false, signal } = options;

  const url = new URL(`${BASE_URL}/api${path}`, window.location.origin);
  if (locale) url.searchParams.set("locale", locale);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (admin) {
    const token = getAdminToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      payload?.error.code ?? "UNKNOWN",
      payload?.error.message ?? "Something went wrong. Please try again.",
      payload?.error.fields,
    );
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json()) as { data: T };
  return payload.data;
}
